import { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Row, Col } from 'react-bootstrap';
import { injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import messages from './MessageCenter.messages';
import * as Sentry from "@sentry/react";
import { useLocation, useNavigate } from "react-router-dom";

// components
import MessageCard from '../../components/MessageCard';
import MessageCardSkeleton from '../../components/MessageCardSkeleton';
import AlertDialog from '../../components/AlertDialog';
import MessageContent from '../../components/MessageContent';
import useMessages from "../../hooks/useMessages";
import useScreenSize from "../../hooks/useScreenSize";
import MessageSortDropdown from "../../components/MessageSortDropdown";
import useEnv from "../../hooks/useEnv";
import useUserAccount from "../../hooks/useUserAccount";

const MOBILE_WIDTH = 992;
const SKELETON_COUNT = 5;

const MessageCenter = (props) => {
    const lng = props.intl.formatMessage;
    const navigate = useNavigate();
    const location = useLocation();
    const { fetchMessages, messageCenterMessages, setMessageUnread, sortMessages } = useMessages();
    const [currentMessage, setCurrentMessage] = useState(null);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const [sortOption, setSortOption] = useState('newest'); // state for sort option
    const messageRefs = useRef({});
    const targetUrl = location.state?.fromJob ? `/job` : '/jobsearch';
    const [files, setFiles] = useState([]); // State for attachments
    const { width } = useScreenSize();
    const isMobile = width < MOBILE_WIDTH;
    const { isEnvTruthy, isDev } = useEnv();
    const { userIsLoggedIn } = useUserAccount();

    useEffect(() => {
        if (!isEnvTruthy('MESSAGECENTER')) {
            // redirect to jobsearch, if message center is not enabled in the environment
            const alert = { message: lng(messages.disabledInEnvironment), type: 'primary' };
            navigate("/jobsearch", { state: { alert } });
        } else if (userIsLoggedIn()) {
            // message center is enabled and user is logged in, get all user's messages
            if (props.maintenance) {
                showAlerts({
                    message: lng(messages.underMaintenance),
                    type: 'warning'
                });
            }
        }
    }, [props.maintenance]);

    useEffect(() => {
        if (location.state?.alert) {
            setAlertMessage(location.state.alert.message);
            setAlertType(location.state.alert.type);
            setShowAlert(true);
        }   
    }, [location.state]);

    /**
     * Update current message.
     */
    useEffect(() => {
        const sortedMessages = sortMessages(messageCenterMessages, sortOption);
        let selectedMessage = null;
        if (!isMobile && sortedMessages.length > 0) {
            selectedMessage = sortedMessages[0];
        }
        let currentMessageId = currentMessage?.id;
        if (currentMessageId === null || currentMessageId === undefined) {
            const queryParams = new URLSearchParams(location.search);
            currentMessageId = queryParams.get('messageId');
        }
        if (currentMessageId) {
            selectedMessage = messageCenterMessages.find(message => message.id === currentMessageId);
        }
        if (selectedMessage) {
            setCurrentMessage(selectedMessage);
            if (selectedMessage.current_status?.unread === 1) {
                setMessageUnread(selectedMessage.id).then(data => {
                    if (isDev) {
                        console.log(`Message ${selectedMessage.id} marked as read`, data);
                    }
                });
            }
            // Scroll to the active message card
            if (messageRefs.current[selectedMessage.id]) {
                messageRefs.current[selectedMessage.id].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [messageCenterMessages]);

    /**
     * Refresh all messages.
     */
    useEffect(() => {
        const getMessages = async () => {
            setCurrentMessage(null);
            setLoadingTickets(true);
            try {
                // Refresh storage
                const allStoredMessages = await fetchMessages();
                if (allStoredMessages.length === 0) {
                    const msg = isEnvTruthy('SHOW_CHAT')
                        ? lng(messages.noMessagesSuffix)
                        : lng(messages.noMessagesSuffixWithoutChat);
                    showAlerts({
                        message: `${lng(messages.noMessagesPrefix)} "${lng(messages.createNewMessage)}" ${msg}`,
                        type: 'primary'
                    });
                }
            } catch (error) {
                Sentry.captureException(error);
                // show error message
                showAlerts({
                    message: lng(messages.errorOccurredTryAgain),
                    type: 'danger'
                });
            } finally {
                // Toggle skeleton loading off
                setLoadingTickets(false);
            }
        }
        if (!props.maintenance) {
            void getMessages();
        }
    }, [props.maintenance]);

    /**
     * Set alert message and type, and show alertDialog.
     *
     * @param {*} alert
     */
    const showAlerts = (alert = null) => {
        setAlertMessage(alert?.message ?? '');
        setAlertType(alert?.type ?? '');
        setShowAlert(!!alert);
    }

    /**
     * Handle message card clicks.
     *
     * @param {string} messageId
     */
    const handleMessageCardClick = (messageId) => {
        showAlerts(); // Clear alerts
        const clickedMessage = messageCenterMessages.find(message => message.id === messageId);
        setCurrentMessage(clickedMessage ?? null);
        if (clickedMessage.current_status?.unread === 1) {
            setMessageUnread(clickedMessage.id).then(data => {
                if (isDev) {
                    console.log(`Message ${clickedMessage.id} marked as read`, data);
                }
            });
        }
    }

    const handleNavigation = () => {
        if (isMobile && currentMessage !== null) {
            setCurrentMessage(null);
        } else {
            navigate(targetUrl);
        }
    }

    const skeletonElement = <>
        <Col xs={12} lg={4}>
            <MessageCardSkeleton count={SKELETON_COUNT} />
        </Col>
        <Col xs={12} lg={8} className="text-center">
            <MessageCardSkeleton count={1} />
        </Col>
    </>

    const messageListElement = <aside aria-label={lng(messages.messageList)}>
        <div aria-labelledby="message-center-title">
            {sortMessages(messageCenterMessages, sortOption).map((message, index, items) => {
                const nextIsActive = index < items.length - 1 && currentMessage?.id === items[index + 1].id;
                const isActive = message.id === currentMessage?.id;
                return (
                    <div key={'message-' + message.id} ref={el => messageRefs.current[message.id] = el}>
                        <button
                            id={"message-" + message.id}
                            className="btn messagecard"
                            onClick={() => handleMessageCardClick(message.id)}
                        >
                            <MessageCard
                                message={message}
                                active={isActive}
                                showAlerts={showAlerts}
                            />
                        </button>
                        {/* do not show spacers before/after the chosen card*/}
                        <hr className={'mb-3 ' + (isActive || nextIsActive ? 'hide' : '') + " d-none d-sm-block"} aria-hidden="true" />
                    </div>
                );
            })}
        </div>
    </aside>

    // Back button header element
    const headerElement = <Row className='ps-4 pb-0 ps-sm-0 pb-sm-1 pt-3 pb-md-2'>
        <Col xs={11}>
            <button
                className='btn text-start p-0'
                aria-label={lng(messages.goBack)}
                onClick={handleNavigation}>
                <h5>
                    <FontAwesomeIcon icon={far.faArrowLeft} aria-hidden="true" /> &nbsp;
                    {lng(messages.goBack)}
                </h5>
            </button>
        </Col>
    </Row>

    // Title and sort dropdown element
    const titleElement = <Row className={`mb-3 mt-3 mt-sm-0 ${currentMessage === null ? '' : 'd-none'} d-lg-block`} aria-labelledby="message-center-title">
        <Col xs={12} lg={4} >
            <Row className='pe-3'>
                <Col xs={8} sm={7} md={8} className='welcome-message pe-0'>
                    <h2 className='text-break ps-4 mb-0 ps-sm-0 ps-xl-4'
                        id="message-center-title"
                    >
                        {lng(messages.title)}</h2>
                </Col>
                {!props.maintenance && messageCenterMessages.length > 0 && (
                    <Col xs={4} sm={5} md={4} className='d-flex justify-content-end ps-0 pe-4 pe-sm-0'>
                        <MessageSortDropdown
                            sortOption={sortOption}
                            onSortChange={setSortOption}
                        />
                    </Col>
                )}
            </Row>
        </Col>
    </Row>

    if (!userIsLoggedIn()) {
        return <></>;
    }

    return (
        <div className="content flex-fill">
            <Helmet id='job-helmet'>
                <title>{lng(messages.messageCenter)} | MyRopo</title>
            </Helmet>
            <Container id='message-center' className={'content flex-fill p-0 p-sm-4 pt-xl-4 pt-sm-0' + (showAlert ? ' alert-shown' : '')}>
                {headerElement}
                {titleElement}
                <div className='ps-4 pe-4 ps-sm-0 pe-sm-0'>
                    {showAlert && (
                        <AlertDialog
                            type={alertType}
                            message={alertMessage}
                        />
                    )}
                </div>
                <Row className='g-3'>
                    {loadingTickets && skeletonElement}
                    {!loadingTickets && !props.maintenance && messageCenterMessages.length > 0 && (
                        <>
                            <Col xs={12} lg={4} className={`message-container ${currentMessage === null ? '' : 'd-none'} d-lg-block`}>
                                {messageListElement}
                            </Col>
                            <Col xs={12} lg={8} className="message-container">
                                {currentMessage !== null && (
                                    <>
                                        <div className='d-lg-none'>
                                            <MessageCard
                                                message={currentMessage}
                                                active={false}
                                                showAlerts={showAlerts}
                                            />
                                        </div>
                                        <MessageContent
                                            message={currentMessage}
                                            showAlerts={showAlerts}
                                            formId='message-center-form'
                                            files={files}
                                            setAttachments={setFiles}
                                        />
                                    </>
                                )}
                            </Col>
                        </>
                    )}
                </Row>
            </Container>
        </div>
    );
};

MessageCenter.propTypes = {
    'intl': PropTypes.object.isRequired,
    'maintenance': PropTypes.bool.isRequired,
};

export default injectIntl(MessageCenter);
